import { useState, useEffect, useRef, useMemo } from 'react';
import { Socket, io } from 'socket.io-client';

interface SocketIOOptions {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onConnectError?: (error: Error) => void;
  onError?: (error: Error) => void;
  onEvent?: (data: string) => void;
  auth?: { token?: string } | Record<string, any>;
  query?: Record<string, string>;
  path?: string;
  autoConnect?: boolean;
}

/**
 * Socket.IO connection states mapped to WebSocket-like readyState values
 * 0: Connecting/Disconnected
 * 1: Connected
 * 2: Disconnected
 */
interface SocketIOApi {
  readyState: 0 | 1 | 2 | number;
  socket: Socket | null;
  connect: () => void;
  disconnect: () => void;
}

export function useWebSocket(url: string, options: SocketIOOptions = {}): SocketIOApi {
  const [readyState, setReadyState] = useState<number>(2); // Start as disconnected (3)
  const socketRef = useRef<Socket | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const socket = io(url, {
      path: options.path || '/socket.io/',
      autoConnect: options.autoConnect ?? false,
      auth: options.auth || {},
      query: options.query || {},
    });

    socketRef.current = socket;
    setReadyState(0); // Connecting

    socket.on('connect', () => {
      setReadyState(1); // Connected
      optionsRef.current.onConnect?.();
    });

    socket.on('disconnect', (reason: string) => {
      setReadyState(2); // Disconnected
      optionsRef.current.onDisconnect?.(reason);
    });

    socket.on('connect_error', (error: Error) => {
      setReadyState(2); // Disconnected due to error
      optionsRef.current.onConnectError?.(error);
    });

    socket.on('error', (error: Error) => {
      optionsRef.current.onError?.(error);
    });

    socket.on('event', (data: string) => {
      optionsRef.current.onEvent?.(data);
    });

    if (options.autoConnect !== false) {
      socket.connect();
    }

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // only keep dependencies that affect connection identiy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, options.path]);

  // Update auth and query when they change
  useEffect(() => {
    if (!socketRef.current) return;

    if (options.auth) {
      socketRef.current.auth = options.auth;
    }
    if (options.query) {
      (socketRef.current.io as any).opts.query = options.query;
    }
  }, [options.auth, options.query]);

  const connect = () => {
    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.connect();
    }
  };

  const disconnect = () => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.disconnect();
    }
  };

  return {
    readyState,
    socket: socketRef.current,
    connect,
    disconnect,
  };
}

export function useLiveSocket(
  jobsiteId: string,
  token?: string,
  callbacks?: {
    onConnect?: () => void;
    onDisconnect?: (reason: string) => void;
    onConnectError?: (error: Error) => void;
    onEvent?: (data: string) => void;
  }
) {
  const url = useMemo(() => {
    return window.location.origin;
  }, []);

  const socketOptions = useMemo(
    () => ({
      path: '/api/live.socket',
      autoConnect: false, // Connect manually after getting token
      auth: token ? { token } : {},
      query: { jobsiteId },
      ...callbacks,
    }),
    [jobsiteId, token, callbacks]
  );

  return useWebSocket(url, socketOptions);
}

export function useSelfServiceSocket(
  jobsiteId: string,
  sessionId: string,
  callbacks?: {
    onConnect?: () => void;
    onDisconnect?: (reason: string) => void;
    onConnectError?: (error: Error) => void;
    onEvent?: (data: string) => void;
  }
) {
  const url = useMemo(() => {
    return window.location.origin.replace(/^http/, 'ws');
  }, []);

  const query = useMemo(() => ({ jobsiteId, sessionId }), [jobsiteId, sessionId]);

  return useWebSocket(url, {
    path: '/api/self-service/live.socket',
    autoConnect: true,
    query,
    ...callbacks,
  });
}
